import type { ICreateProjectStepBody, IProjectStepResource, IUpdateProjectStepBody } from "../../../../../../api/types"
import { formatApiDateToDdMmYyyy } from "../../../../../../utils/common"
import { TActionPlanFormValues, TActionPlanItem } from "../../ZodValidationSchema/ActionPlanSchema"

export const formatStepDateForForm = (value: string | null | undefined): string =>
    formatApiDateToDdMmYyyy(value) || ""

// берём DD.MM.YYYY → YYYY-MM-DD для базы данных/API
export const parseSingleFormDateToApi = (value: string): string | null => {
    const [dd, mm, yyyy] = value.trim().split(".")
    if (!dd || !mm || !yyyy) return null
    return `${yyyy}-${mm}-${dd}`
}

export const mapResponsiblesToUserIds = (responsibles: TActionPlanItem["responsibles"]): number[] =>
    responsibles.map((item) => item.id)

/** берём shortname → user_id для базы данных */
export const mapResponsibleToUserId = (responsible: TActionPlanItem["responsibles"][number]): number =>
    responsible.id

export const mapStepResourceToFormItem = (step: IProjectStepResource): TActionPlanItem => ({
    stepId: step.id,
    status: step.status,
    title: step.name,
    date_from: formatStepDateForForm(step.started_at),
    date_to: formatStepDateForForm(step.finished_at),
    description: step.description,
    responsibles: (step.responsibles ?? []).map((user) => ({
        id: user.id,
        label: user.shortname,
    })),
})

export const mapFormItemToCreateBody = (item: TActionPlanItem): ICreateProjectStepBody => ({
    name: item.title,
    description: item.description,
    started_at: parseSingleFormDateToApi(item.date_from),
    finished_at: parseSingleFormDateToApi(item.date_to),
    responsible_user_ids: mapResponsiblesToUserIds(item.responsibles),
    status: "draft",
})

export const mapFormItemToUpdateBody = (item: TActionPlanItem): IUpdateProjectStepBody => ({
    name: item.title,
    description: item.description,
    started_at: parseSingleFormDateToApi(item.date_from),
    finished_at: parseSingleFormDateToApi(item.date_to),
    responsible_user_ids: mapResponsiblesToUserIds(item.responsibles),
    ...(item.status ? { status: item.status } : {}),
})

const responsiblesEqual = (
    a: TActionPlanItem["responsibles"],
    b: TActionPlanItem["responsibles"],
): boolean => {
    const idsA = mapResponsiblesToUserIds(a).slice().sort((left, right) => left - right)
    const idsB = mapResponsiblesToUserIds(b).slice().sort((left, right) => left - right)

    if (idsA.length !== idsB.length) {
        return false
    }

    return idsA.every((id, index) => id === idsB[index])
}

export const areActionPlanItemsEqual = (a: TActionPlanItem, b: TActionPlanItem): boolean => {
    if (a.stepId !== b.stepId) return false
    if (a.status !== b.status) return false
    if (a.title.trim() !== b.title.trim()) return false
    if (a.date_from.trim() !== b.date_from.trim()) return false
    if (a.date_to.trim() !== b.date_to.trim()) return false
    if (a.description.trim() !== b.description.trim()) return false
    if (!responsiblesEqual(a.responsibles, b.responsibles)) return false
    return true
}

export const areActionPlanFormValuesEqual = (
    a: TActionPlanFormValues,
    b: TActionPlanFormValues,
): boolean => {
    if (a.actionPlan.length !== b.actionPlan.length) return false
    for (let i = 0; i < a.actionPlan.length; i++) {
        if (!areActionPlanItemsEqual(a.actionPlan[i], b.actionPlan[i])) {
            return false
        }
    }
    return true
}
